import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ImagePlus } from "lucide-react";
import { IMG } from "./siteData";

// Change this when the Nayara Fab app has its public link.
export const TRY_FAB_URL = "/studio";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_KEY = "nayara_site_token";
const siteHeaders = () => { const t = localStorage.getItem(SITE_KEY); return t ? { Authorization: `Bearer ${t}` } : {}; };

function useSiteImages() {
  const [cfg, setCfg] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  const load = () => axios.get(`${API}/site/settings`).then((r) => setCfg(r.data || {})).catch(() => {});
  useEffect(() => {
    load();
    if (localStorage.getItem(SITE_KEY) && new URLSearchParams(window.location.search).has("admin")) axios.get(`${API}/site/admin/me`, { headers: siteHeaders() }).then(() => setIsAdmin(true)).catch(() => {});
  }, []);
  const src = (slot, fallback) => (cfg[slot] ? `${API}/site/fabric-images/${cfg[slot]}` : fallback);
  return { cfg, isAdmin, src, reload: load };
}

function ChangePhoto({ slot, onDone, label = "Change photo", className = "" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("slot", slot); fd.append("image", f);
    try { await axios.post(`${API}/site/settings/image`, fd, { headers: siteHeaders() }); onDone(); }
    catch (err) { alert(err.response?.data?.detail || "Upload aagala"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <>
      <button type="button" onClick={() => ref.current?.click()} aria-label={label}
        className={`z-20 inline-flex items-center gap-2 rounded-full bg-[#2B1A10]/85 px-4 py-2.5 text-[10.5px] uppercase tracking-[0.2em] text-[#F4E7D3] shadow-lg backdrop-blur transition hover:bg-[#2B1A10] ${className}`}>
        <ImagePlus className="h-4 w-4" />{busy ? "Uploading..." : label}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
    </>
  );
}

const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const SCRIPT = { fontFamily: "'Pinyon Script', cursive" };
const ease = [0.2, 0.7, 0.2, 1];
const up = (d) => ({ initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 1.1, delay: d, ease } });

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Pinyon+Script&display=swap');
@keyframes nyDraw{to{stroke-dashoffset:0}}
@keyframes nyLeaf{from{opacity:0;transform:scale(.6)}to{opacity:1;transform:none}}
@keyframes nySpin{to{transform:rotate(360deg)}}
@keyframes nyFloat{0%,100%{transform:translateY(0) rotate(-6deg)}50%{transform:translateY(-12px) rotate(-6deg)}}
@keyframes nyMarquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
@keyframes nyShine{0%{background-position:-160% 0}55%,100%{background-position:260% 0}}
@keyframes nyScroll{0%{transform:translateY(0);opacity:0}30%{opacity:1}100%{transform:translateY(18px);opacity:0}}
.ny-draw{stroke-dasharray:1200;stroke-dashoffset:1200;animation:nyDraw 3.4s .8s cubic-bezier(.4,0,.2,1) forwards}
.ny-leaf{opacity:0;transform-box:fill-box;transform-origin:left bottom;animation:nyLeaf .9s cubic-bezier(.2,.7,.2,1) forwards}
.ny-spin{animation:nySpin 24s linear infinite}
.ny-float{animation:nyFloat 8s ease-in-out infinite}
.ny-marquee{animation:nyMarquee 45s linear infinite}
.ny-shine{background-image:linear-gradient(110deg,transparent 30%,rgba(255,236,200,.45) 50%,transparent 70%);background-size:200% 100%;animation:nyShine 4s ease-in-out infinite}
.ny-scroll{animation:nyScroll 2.2s ease-in-out infinite}
@media (prefers-reduced-motion:reduce){.ny-draw{animation:none;stroke-dashoffset:0}.ny-leaf{animation:none;opacity:1}.ny-spin,.ny-float,.ny-marquee,.ny-shine,.ny-scroll{animation:none}}
`;

export function BrandLogo({ className = "h-14" }) {
  const [ok, setOk] = useState(true);
  if (ok) return <img src="/nayara-logo.png" alt="Nayara Studio" className={`${className} w-auto`} onError={() => setOk(false)} />;
  return (
    <span className="flex flex-col items-center leading-none text-[#7B4520]">
      <span style={{ ...SCRIPT, fontSize: 38 }}>Nayara</span>
      <span className="mt-0.5 text-[9px] tracking-[0.5em]">STUDIO</span>
    </span>
  );
}

export function TryFabButton() {
  return (
    <a href={TRY_FAB_URL} target="_blank" rel="noreferrer" data-testid="try-nayara-fab"
      className="ny-shine hidden items-center gap-2.5 rounded-full border border-[#C9A27A] bg-[#2B1A10] py-2.5 pl-4 pr-5 text-[11px] font-medium uppercase tracking-[0.16em] text-[#F4E7D3] transition-transform duration-500 hover:-translate-y-0.5 sm:inline-flex">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D9B98E" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><path d="M4 20c4-1 9-5 11-12" /><path d="M15 8c-3 0-5 2-5 5 3 0 5-2 5-5z" /><path d="M12 13c-2.5-.5-4.5.5-5.5 3 2.5.5 4.5-.5 5.5-3z" /><path d="M15 8c.5-2.5 2-4 4.5-4.5-.2 2.6-1.8 4.2-4.5 4.5z" /></svg>
      Try Nayara Fab
    </a>
  );
}

const MARQ = ["Printed Fabrics", "Custom Textiles", "Event Fabrics", "Professional Backdrops", "Digital Sublimation"];

export default function PremiumHero() {
  const { isAdmin, src, reload } = useSiteImages();
  return (
    <section className="relative overflow-hidden bg-[#FBF7F1] text-[#3A2416]" data-testid="premium-hero">
      <style>{CSS}</style>
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(60% 50% at 78% 30%, rgba(201,162,122,.18), transparent 70%), radial-gradient(40% 40% at 10% 90%, rgba(123,69,32,.07), transparent 70%)" }} />

      <div className="relative mx-auto grid max-w-[1400px] items-center gap-16 px-6 pb-14 pt-8 lg:grid-cols-2 lg:px-12 lg:pt-12">
        <div className="flex flex-col gap-8">
          <motion.div {...up(0.3)} className="flex items-center gap-3.5 text-[10.5px] uppercase tracking-[0.4em] text-[#7B4520]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#7B4520]" /><span className="h-px w-12 bg-[#7B4520]" />Digital Textile Studio · Tiruppur
          </motion.div>
          <h1 className="text-[clamp(48px,5.6vw,88px)] font-light leading-none tracking-[-0.015em] text-[#2B1A10]" style={SERIF}>
            <motion.span {...up(0.45)} className="block">Where every</motion.span>
            <motion.span {...up(0.6)} className="block">thread tells a</motion.span>
            <motion.span {...up(0.75)} className="block pl-[0.4em] text-[1.22em] leading-[1.05] text-[#7B4520]" style={SCRIPT}>beautiful story</motion.span>
          </h1>
          <motion.p {...up(0.95)} className="max-w-[500px] text-base font-light leading-[1.85] text-[#5E4634]">
            We manufacture premium printed fabrics, custom textiles, event fabrics and professional backdrops using advanced digital textile printing technology.
          </motion.p>
          <motion.div {...up(1.1)} className="flex flex-wrap items-center gap-4">
            <Link to="/collections" className="group inline-flex items-center gap-3 bg-[#7B4520] px-8 py-[18px] text-xs font-medium uppercase tracking-[0.22em] text-[#FBF7F1] transition-all duration-500 hover:-translate-y-0.5 hover:shadow-[0_18px_34px_-18px_rgba(58,36,22,.6)]" data-testid="hero-explore-btn">
              Explore Collections <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
            </Link>
            <Link to="/contact" className="inline-flex items-center border border-[#7B4520] px-8 py-[17px] text-xs font-medium uppercase tracking-[0.22em] text-[#7B4520] transition-colors duration-500 hover:bg-[#7B4520] hover:text-[#FBF7F1]" data-testid="hero-quote-btn">Request a Quote</Link>
          </motion.div>
          <motion.div {...up(1.3)} className="grid max-w-[520px] grid-cols-3 gap-5 border-t border-[#7B4520]/15 pt-6">
            {[["In-house", "Printing"], ["Bespoke", "Designs"], ["Sample", "to Bulk"]].map(([a, b]) => (
              <div key={a}><div className="text-[22px] italic text-[#2B1A10]" style={SERIF}>{a}</div><div className="mt-1 text-[10.5px] uppercase tracking-[0.2em] text-[#8A6E58]">{b}</div></div>
            ))}
          </motion.div>
        </div>

        <div className="relative min-h-[520px] lg:min-h-[640px]">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 1.6 }}
            className="absolute bottom-[34px] left-[70px] right-0 top-0 rounded-t-[260px] border border-[#C9A27A]" />
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1.6 }}
            className="absolute bottom-0 left-[34px] right-[36px] top-[34px] overflow-hidden rounded-t-[260px] shadow-[0_50px_90px_-50px_rgba(43,26,16,.7)]">
            <motion.img key={src("hero", IMG.hero)} src={src("hero", IMG.hero)} alt="Nayara signature printed fabric" className="h-full w-full object-cover"
              initial={{ scale: 1.14 }} animate={{ scale: 1 }} transition={{ duration: 10, ease: "easeOut" }} />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#2B1A10]/65" />
            <div className="absolute bottom-8 left-9 right-9 text-right text-[#FBF7F1]">
              <div className="text-[10px] uppercase tracking-[0.32em] opacity-80">Our signature</div>
              <div className="mt-1.5 text-[40px] leading-none" style={SCRIPT}>Signature Prints</div>
            </div>
          </motion.div>
          {isAdmin && <ChangePhoto slot="hero" onDone={reload} label="Change hero photo" className="absolute right-12 top-16" />}

          <svg viewBox="0 0 220 260" className="absolute -left-9 top-28 h-[225px] w-[190px]" aria-hidden="true">
            <path className="ny-draw" d="M20 250 C 60 200, 90 150, 110 90 S 150 20, 190 6" fill="none" stroke="#7B4520" strokeWidth="1.4" strokeLinecap="round" />
            <path className="ny-leaf" style={{ animationDelay: "2.2s" }} d="M70 180 C 40 170, 28 148, 30 128 C 52 132, 70 152, 70 180 Z" fill="none" stroke="#7B4520" strokeWidth="1.3" />
            <path className="ny-leaf" style={{ animationDelay: "2.5s" }} d="M98 120 C 118 104, 142 104, 158 114 C 142 132, 118 134, 98 120 Z" fill="none" stroke="#7B4520" strokeWidth="1.3" />
            <path className="ny-leaf" style={{ animationDelay: "2.8s" }} d="M120 70 C 104 52, 104 30, 112 14 C 128 30, 132 52, 120 70 Z" fill="none" stroke="#7B4520" strokeWidth="1.3" />
            <path className="ny-leaf" style={{ animationDelay: "3.1s" }} d="M160 30 C 172 14, 190 8, 206 10 C 196 26, 180 34, 160 30 Z" fill="none" stroke="#7B4520" strokeWidth="1.3" />
          </svg>

          <div className="absolute -right-1.5 bottom-32 flex h-[132px] w-[132px] items-center justify-center rounded-full bg-[#FBF7F1] shadow-[0_20px_40px_-24px_rgba(43,26,16,.6)]">
            <svg className="ny-spin absolute inset-2 h-[116px] w-[116px]" viewBox="0 0 120 120" aria-hidden="true">
              <defs><path id="nyCircle" d="M60 60 m-46 0 a46 46 0 1 1 92 0 a46 46 0 1 1 -92 0" /></defs>
              <text style={{ fontSize: "9.2px", letterSpacing: "2.1px", fill: "#7B4520", textTransform: "uppercase" }}><textPath href="#nyCircle">Printed in Tiruppur · Nayara Studio · </textPath></text>
            </svg>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#7B4520" strokeWidth="1.2" strokeLinecap="round" aria-hidden="true"><path d="M4 20c4-1 9-5 11-12" /><path d="M15 8c-3 0-5 2-5 5 3 0 5-2 5-5z" /><path d="M12 13c-2.5-.5-4.5.5-5.5 3 2.5.5 4.5-.5 5.5-3z" /></svg>
          </div>

          <div className="ny-float absolute -left-6 bottom-10 w-[150px] bg-white p-2.5 pb-3.5 shadow-[0_26px_44px_-24px_rgba(43,26,16,.55)]">
            <img src={src("polaroid", IMG.printedFolds || IMG.hero)} alt="" className="h-[118px] w-full object-cover" />
            {isAdmin && <ChangePhoto slot="polaroid" onDone={reload} label="Change" className="absolute left-1/2 top-12 -translate-x-1/2 !px-3 !py-2" />}
            <div className="mt-2.5 text-base italic text-[#3A2416]" style={SERIF}>New prints weekly</div>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center gap-2.5 pb-7 text-[10px] uppercase tracking-[0.36em] text-[#8A6E58]">
        <span>Scroll</span>
        <span className="relative h-[34px] w-px overflow-hidden bg-[#7B4520]/25"><span className="ny-scroll absolute left-0 top-0 h-3.5 w-px bg-[#7B4520]" /></span>
      </div>

      <div className="overflow-hidden border-y border-[#7B4520]/15 bg-[#F3EADD]">
        <div className="ny-marquee flex w-max items-center gap-[60px] whitespace-nowrap py-6 text-[28px] font-light italic text-[#5E4634]" style={SERIF}>
          {[...MARQ, ...MARQ].map((m, i) => (
            <React.Fragment key={i}><span>{m}</span><span className="not-italic text-[#7B4520]" style={SCRIPT}>&amp;</span></React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
